import React from "react"
import ReactDOM from "react-dom/client"
import { RouterProvider } from "react-router/dom"
import { createBrowserRouter, Outlet, redirect, type IndexRouteObject, type NonIndexRouteObject } from "react-router"
import { setNonce } from "get-nonce" // strictly for @radix-ui's dependency on react-remove-scroll

import "@/index.css"

import { Root } from "@/routes/root"
import { Applications } from "@/routes/applications"
import { Application } from "@/routes/applications-$name"
import { ApplicationSections } from "@/routes/applications-$name-sections"

// Unity Viewer
import { Tags } from "@/routes/viewer/tags"
import { TagTypes } from "@/routes/viewer/tag-types"
import { UserExclusions } from "@/routes/viewer/audit-user-exclusion"
import { BrandEvidenceThemes } from "@/routes/viewer/brand-evidence-themes"
import { EvidenceThemeThresholds } from "@/routes/viewer/brand-evidence-theme-thresholds"
import { BrandDocumentClassification } from "@/routes/viewer/brand-document-classifications"
import { BrandDocumentClassificationEvidenceThemes } from "@/routes/viewer/brand-document-classification-evidence-theme"
import { VectorThresholds } from "./routes/viewer/brand-vector-thresholds"
import { EvidenceCounts } from "./routes/viewer/brand-evidence-count"
import { BrandLenses } from "./routes/viewer/Brand-Lenses/brand-lenses"
import { LensConfiguration } from "./routes/viewer/Brand-Lenses/brand-lenses-configuration"
import { Retention } from "./routes/viewer/retention"
import { SummarizerConfiguration } from "./routes/viewer/summarizer/summarizer-configuration"
import { SummarizerTest } from "./routes/viewer/summarizer/SummarizerTest"
import { BrandRules } from "./routes/viewer/brand-rules"

import { loadConfig } from "@/utils/config"
import { setupAuthMachine } from "@/machines/Authmachine"

import { InfoCardProvider } from "./components/infoCard"
import { Auth } from "./routes/auth"
import { BrandConfigManager } from "./routes/viewer/brandUIConfig/brand-ui-config"
import { BrandUserGroups } from "./routes/viewer/brand-user-group"

import type { AppHandle } from "@/utils/types"

import { AppInsightsContext } from "@microsoft/applicationinsights-react-js"
import { createInsightsSettings } from "./insights"
import { MetadataUsage } from "./routes/viewer/metaData-usage"
import { Guidance } from "./routes/reviewer/guidance"
import { GuidanceForm } from "./routes/reviewer/guidance-form"
import { Checks } from "./routes/reviewer/Checks"
import { RootCauses } from "./routes/reviewer/review-rootCause"
import { ReviewQuestions } from "./routes/reviewer/review-questions"
import { States } from "./routes/reviewer/states-form"
import { CheckForm } from "./routes/reviewer/checks-form"

const nonceEl = document.querySelector("meta[name=nonce]")
setNonce(nonceEl?.getAttribute("content") ?? "")
nonceEl?.remove()

await loadConfig()
setupAuthMachine()

// Allow the types to be accessed in any file without importing
declare global {
  // Add the expected Handle type to Index routes
  type IndexRouteObjectWithBreadCrumbHandle = Omit<IndexRouteObject, "handle"> & { handle?: AppHandle }

  // Add the expected Handle type to Non-Index routes and all its children
  type NonIndexRouteObjectWithBreadCrumbHandle = Omit<NonIndexRouteObject, "handle" | "children"> & {
    handle?: AppHandle
    children?: RouteObject[]
  }

  // Override the default RouteObject so they all have the handle property safely typed
  type RouteObject = IndexRouteObjectWithBreadCrumbHandle | NonIndexRouteObjectWithBreadCrumbHandle
}

const router = createBrowserRouter([
  {
    path: "/",
    id: Root.routeId,
    element: <Root />,
    loader: Root.loader,
    errorElement: <Root.ErrorBoundary />,
    hydrateFallbackElement: <div className="grid h-screen w-screen place-items-center">Loading...</div>,
    children: [
      { index: true, element: null, loader: () => redirect("applications") },
      {
        id: "applications",
        path: "applications",
        element: <Applications />,
        errorElement: <Root.ErrorBoundary />,
        children: [
          {
            index: true,
            element: <Applications.Main />,
            loader: Applications.loader,
            errorElement: <Root.ErrorBoundary />,
          },
        ],
      },
      {
        path: "applications/:applicationName",
        element: <Application />,
        errorElement: <Root.ErrorBoundary />,
        handle: { breadcrumb: (match) => match.params.applicationName! },
        children: [
          {
            path: "",
            element: <ApplicationSections />,
            errorElement: <Root.ErrorBoundary />,
            children: [
              {
                index: true,
                element: <ApplicationSections.Main />,
                loader: ApplicationSections.loader,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
        ],
      },
      {
        handle: { app: "Unity Viewer" },
        element: (
          <div className="flex size-full flex-col gap-y-3 overflow-x-auto overflow-y-hidden px-4 py-5 sm:px-8 lg:px-20">
            <Outlet />
          </div>
        ),
        children: [
          {
            path: "applications/Unity Viewer/audit-user-exclusion",
            element: <UserExclusions />,
            action: UserExclusions.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Audit User Exclusion" },
            children: [
              {
                index: true,
                id: UserExclusions.routeId,
                element: <UserExclusions.Main />,
                loader: UserExclusions.loader,
                action: UserExclusions.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/tag-types",
            element: <TagTypes />,
            action: TagTypes.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Tag Types" },
            children: [
              {
                index: true,
                id: TagTypes.routeId,
                element: <TagTypes.Main />,
                loader: TagTypes.loader,
                action: TagTypes.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/tags",
            element: <Tags />,
            action: Tags.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Tags" },
            children: [
              {
                index: true,
                id: Tags.routeId,
                element: <Tags.Main />,
                loader: Tags.loader,
                action: Tags.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/retention",
            element: <Retention />,
            errorElement: <Root.ErrorBoundary />,
            action: Retention.action,
            handle: { breadcrumb: () => "Retention" },
            children: [
              {
                index: true,
                id: Retention.routeId,
                element: <Retention.Main />,
                loader: Retention.loader,
                action: Retention.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-evidence-themes",
            element: <BrandEvidenceThemes />,
            action: BrandEvidenceThemes.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Brand Evidence Themes" },
            children: [
              {
                index: true,
                id: BrandEvidenceThemes.routeId,
                element: <BrandEvidenceThemes.Main />,
                loader: BrandEvidenceThemes.loader,
                action: BrandEvidenceThemes.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-evidence-theme-thresholds",
            element: <EvidenceThemeThresholds />,
            action: EvidenceThemeThresholds.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Evidence Theme Thresholds" },
            children: [
              {
                index: true,
                id: EvidenceThemeThresholds.routeId,
                element: <EvidenceThemeThresholds.Main />,
                loader: EvidenceThemeThresholds.loader,
                action: EvidenceThemeThresholds.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-document-classifications",
            element: <BrandDocumentClassification />,
            action: BrandDocumentClassification.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Brand Document Classifications" },
            children: [
              {
                index: true,
                id: BrandDocumentClassification.routeId,
                element: <BrandDocumentClassification.Main />,
                loader: BrandDocumentClassification.loader,
                action: BrandDocumentClassification.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-document-classification-evidence-themes",
            element: <BrandDocumentClassificationEvidenceThemes />,
            action: BrandDocumentClassificationEvidenceThemes.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Brand Document Classification Evidence Themes" },
            children: [
              {
                index: true,
                id: BrandDocumentClassificationEvidenceThemes.routeId,
                element: <BrandDocumentClassificationEvidenceThemes.Main />,
                loader: BrandDocumentClassificationEvidenceThemes.loader,
                action: BrandDocumentClassificationEvidenceThemes.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-vector-thresholds",
            element: <VectorThresholds />,
            action: VectorThresholds.action,
            errorElement: <Root.ErrorBoundary />,
            handle: {
              breadcrumb: () => "Brand Vector Thresholds",
            },
            children: [
              {
                index: true,
                id: VectorThresholds.routeId,
                element: <VectorThresholds.Main />,
                loader: VectorThresholds.loader,
                action: VectorThresholds.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-evidence-counts",
            element: <EvidenceCounts />,
            action: EvidenceCounts.action,
            errorElement: <Root.ErrorBoundary />,
            handle: {
              breadcrumb: () => "Brand Evidence Counts",
            },
            children: [
              {
                index: true,
                id: EvidenceCounts.routeId,
                element: <EvidenceCounts.Main />,
                loader: EvidenceCounts.loader,
                action: EvidenceCounts.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-lenses",
            id: BrandLenses.routeId,
            element: <BrandLenses />,
            loader: BrandLenses.loader,
            action: BrandLenses.action,
            errorElement: <Root.ErrorBoundary />,
            handle: {
              breadcrumb: () => "Brand Lenses",
            },
            children: [
              {
                index: true,
                element: <BrandLenses.List />,
                loader: BrandLenses.loader,
                action: BrandLenses.action,
              },
              {
                path: "configure/:lensCode",
                element: <LensConfiguration />,
                loader: LensConfiguration.loader,
                action: LensConfiguration.action,
                errorElement: <Root.ErrorBoundary />,
                handle: {
                  breadcrumb: () => "Configure Lens",
                },
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-rules",
            element: <BrandRules />,
            action: BrandRules.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Brand Rules" },
            children: [
              {
                index: true,
                id: BrandRules.routeId,
                element: <BrandRules.Main />,
                loader: BrandRules.loader,
                action: BrandRules.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },

          {
            path: "applications/Unity Viewer/brand-user-groups",
            element: <BrandUserGroups />,
            errorElement: <Root.ErrorBoundary />,
            action: BrandUserGroups.action,
            handle: { breadcrumb: () => "Brand User Group" },
            children: [
              {
                index: true,
                id: BrandUserGroups.routeId,
                element: <BrandUserGroups.Main />,
                loader: BrandUserGroups.loader,
                action: BrandUserGroups.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
              {
                path: "configure/:groupId",
                element: <BrandUserGroups.Configuration />,
                loader: BrandUserGroups.configLoader,
                action: BrandUserGroups.configAction,
                errorElement: <Root.ErrorBoundary />,
                handle: {
                  breadcrumb: (match) => {
                    const data = match.loaderData as Awaited<ReturnType<typeof BrandUserGroups.configLoader>>
                    return `Configure ${data.group.tenant} :: ${data.group.product} :: ${data.group.groupName}`
                  },
                },
              },
            ],
          },
          {
            path: "applications/Unity Viewer/brand-client-ui-config",
            element: <BrandConfigManager />,
            id: BrandConfigManager.routeId,
            loader: BrandConfigManager.loader,
            action: BrandConfigManager.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Brand Client UI Config" },
            children: [
              {
                index: true,
                loader: BrandConfigManager.loader,
                action: BrandConfigManager.action,
                shouldRevalidate: () => true,
                element: <BrandConfigManager.BrandList />,
                errorElement: <Root.ErrorBoundary />,
              },
              {
                path: ":brand",
                loader: BrandConfigManager.loader,
                action: BrandConfigManager.action,
                shouldRevalidate: () => true,
                element: <BrandConfigManager.ConfigurationEditor />,
                errorElement: <Root.ErrorBoundary />,
                handle: {
                  breadcrumb: (match) => match.params.brand!,
                },
              },
            ],
          },
          {
            path: "applications/Unity Viewer/metadata-usage",
            element: <MetadataUsage />,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "MetaData Usage" },
            id: MetadataUsage.routeId,
            loader: MetadataUsage.loader,
            action: MetadataUsage.action,
            children: [
              {
                index: true,
                element: <MetadataUsage.Main />,
                action: MetadataUsage.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Viewer/summariser/*",
            loader: ({ request }) => {
              const url = new URL(request.url)
              return redirect(`${url.pathname.replace(/\/summariser(?=\/|$)/i, "/summarizer")}${url.search}${url.hash}`)
            },
          },
          {
            path: "applications/Unity Viewer/summarizer",
            id: SummarizerConfiguration.routeId,
            element: <SummarizerConfiguration />,
            loader: SummarizerConfiguration.loader,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Summarizer Configuration" },
            children: [
              {
                index: true,
                element: <SummarizerConfiguration.Main />,
                errorElement: <Root.ErrorBoundary />,
              },
              {
                path: "test",
                element: <SummarizerTest />,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
        ],
      },
      {
        handle: { app: "Unity Review" },
        element: (
          <div className="flex size-full flex-col gap-y-3 overflow-x-auto overflow-y-hidden px-20 py-5">
            <Outlet />
          </div>
        ),
        children: [
          {
            path: "applications/Unity Review/guidance",
            id: Guidance.routeId,
            element: <Guidance />,
            loader: Guidance.loader,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Guidance" },
            children: [
              {
                index: true,
                element: <Guidance.Main />,
                action: Guidance.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
              {
                path: "new",
                element: <GuidanceForm />,
                action: GuidanceForm.action,
                errorElement: <Root.ErrorBoundary />,
                handle: { breadcrumb: () => "New Guidance" },
              },
              {
                path: ":name/edit",
                element: <GuidanceForm />,
                action: GuidanceForm.action,
                errorElement: <Root.ErrorBoundary />,
                handle: {
                  breadcrumb: (match) => `Edit ${match.params.name}`,
                },
              },
            ],
          },
          {
            path: "applications/Unity Review/checks",
            id: Checks.routeId,
            element: <Checks />,
            loader: Checks.loader,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Checks" },
            children: [
              {
                index: true,
                element: <Checks.Main />,
                action: Checks.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
              {
                path: "create",
                element: <CheckForm />,
                loader: CheckForm.loader,
                action: CheckForm.action,
                errorElement: <Root.ErrorBoundary />,
                handle: { breadcrumb: () => "Create Check" },
              },
              {
                path: ":reference/edit",
                element: <CheckForm />,
                loader: CheckForm.loader,
                action: CheckForm.action,
                errorElement: <Root.ErrorBoundary />,
                handle: { breadcrumb: (match) => `Edit ${match.params.reference}` },
              },
            ],
          },
          {
            path: "applications/Unity Review/questions",
            element: <ReviewQuestions />,
            action: ReviewQuestions.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Review Questions" },
            children: [
              {
                index: true,
                id: ReviewQuestions.routeId,
                element: <ReviewQuestions.Main />,
                loader: ReviewQuestions.loader,
                action: ReviewQuestions.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Review/root-causes",
            element: <RootCauses />,
            action: RootCauses.Main.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "Root Causes" },
            children: [
              {
                index: true,
                id: RootCauses.routeId,
                element: <RootCauses.Main />,
                loader: RootCauses.loader,
                action: RootCauses.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
          {
            path: "applications/Unity Review/states",
            element: <States />,
            action: States.Main.action,
            errorElement: <Root.ErrorBoundary />,
            handle: { breadcrumb: () => "States" },
            children: [
              {
                index: true,
                id: States.routeId,
                element: <States.Main />,
                loader: States.loader,
                action: States.Main.action,
                errorElement: <Root.ErrorBoundary />,
              },
            ],
          },
        ],
      },
    ],
  },
  { path: "/auth", id: Auth.routeId, element: <Auth />, loader: Auth.loader, errorElement: <Auth.ErrorBoundary /> },
  { path: "/external-login", loader: () => (window.close(), null) },
] satisfies RouteObject[])

const { reactPlugin, isTelemetryEnabled } = createInsightsSettings()

const appContent = (
  <InfoCardProvider>
    <RouterProvider router={router} />
  </InfoCardProvider>
)

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {isTelemetryEnabled ? (
      <AppInsightsContext.Provider value={reactPlugin}>{appContent}</AppInsightsContext.Provider>
    ) : (
      appContent
    )}
  </React.StrictMode>,
)
